import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { EllipseSingle } from '@squareup/dex-icons/dex/misc';
// TODO DPW-801: Move ReferenceCodeBlock to a ui library
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReferenceCodeBlock } from '@squareup/dex-tech-ui';
import { HttpStatus, Link } from '@squareup/dex-ui';
import {
  useApiExplorerAccessTokenData,
  useApiExplorerApiResponse,
  useApiExplorerEndpoint,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import clsx from 'clsx';
import React, { FC, useMemo } from 'react';

import styles from './api-explorer-response-content-container.module.css';

const strings = {
  responseTitle: 'Response',
  noResponse: '// No response received yet.',
  viewLogs: 'View logs ->',
};

const filteredHeaders = [
  'cache-control',
  'content-encoding',
  'content-type',
  'date',
  'etag',
  'square-version',
];

interface Props {}

const ApiExplorerResponseContentContainer: FC<Props> = () => {
  const endpoint = useApiExplorerEndpoint();
  const { apiResponse, isApiRequestRunning } = useApiExplorerApiResponse();
  const { environment, productionClient } = useApiExplorerAccessTokenData();

  const { data: userInfo } = getDeveloperApi().useGetUserInfoQuery();
  const isLoggedIn = Boolean(userInfo?.person);

  const headerText = useMemo(() => {
    if (!apiResponse?.headers) {
      return undefined;
    }

    return filteredHeaders
      .map((header) => {
        const value = apiResponse.headers?.[header];
        if (value) {
          return `// ${header}: ${value}`;
        } else {
          return undefined;
        }
      })
      .filter(Boolean)
      .join('\n');
  }, [apiResponse?.headers]);

  const responseText = apiResponse
    ? JSON.stringify(apiResponse.body, null, 2)
    : strings.noResponse;

  const viewLogsLink =
    !isApiRequestRunning &&
    isLoggedIn &&
    apiResponse?.status &&
    productionClient ? (
      <>
        <EllipseSingle
          className={clsx(
            styles['ellipse-divider'],
            commonIconStyles['icon-fill-circle-current-color']
          )}
        />
        <Paragraph20>
          <Link
            className={styles['link']}
            target="_blank"
            trackingId="api-explorer-view-logs-link"
            href={`/apps/${productionClient}/log?environment=${environment.toLowerCase()}`}
          >
            {strings.viewLogs}
          </Link>
        </Paragraph20>
      </>
    ) : null;

  return (
    <ReferenceCodeBlock
      title={
        <Box className={styles['response-title']}>
          <Paragraph20 weight="semi-bold">
            {!isApiRequestRunning && apiResponse?.status && (
              <>
                <HttpStatus status={apiResponse.status} as={'span'} />
                &nbsp;
              </>
            )}
            {strings.responseTitle}
          </Paragraph20>
          {viewLogsLink}
        </Box>
      }
      initialCode={headerText}
      code={responseText}
      currentLanguage={'json'}
      isLoading={isApiRequestRunning}
      fillParentHeight={true}
      entity={endpoint?.responseBody}
      enableAttributeTooltip={true}
    />
  );
};

export { ApiExplorerResponseContentContainer };
